<template>
  <section class="langs-section" aria-labelledby="languages-section-title">
    <div class="langs-inner">
      <h2 id="languages-section-title" class="langs-title u-display">
        {{ $t("home.languagesSection.title") }}
      </h2>
      <!-- WCAG 2.2.2: the rows move automatically for longer than five seconds
           alongside other content, so a pause mechanism that does not require a
           pointer is mandatory; hovering is not reachable by keyboard. The
           label reuses the existing audio.play / audio.pause keys, which are
           already translated in all 31 locales. The control hides itself under
           prefers-reduced-motion, where there is no motion left to pause. -->
      <button
        type="button"
        class="lang-motion-toggle"
        :aria-label="$t(paused ? 'audio.play' : 'audio.pause')"
        @click="paused = !paused"
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <path v-if="paused" d="M8 5v14l11-7z" />
          <template v-else>
            <rect x="6" y="5" width="4" height="14" rx="1.2" />
            <rect x="14" y="5" width="4" height="14" rx="1.2" />
          </template>
        </svg>
      </button>
    </div>
    <div v-for="(row, rowIndex) in rows" :key="rowIndex" class="lang-row">
      <div
        class="lang-track"
        :class="{
          'lang-track-reverse': rowIndex === 1,
          'lang-track-paused': paused,
        }"
        :style="{ '--lang-row-count': row.length }"
      >
        <!-- The track holds the row twice so translating it by exactly half
             its width lands the copy where the original started, which is what
             makes the loop seamless. The second pass is the same 15-16
             languages, so it is hidden from assistive tech; under reduced
             motion it is removed outright and the row simply wraps. -->
        <ul
          v-for="pass in 2"
          :key="pass"
          class="lang-set"
          role="list"
          :aria-hidden="pass === 2 ? 'true' : undefined"
        >
          <li v-for="code in row" :key="code" class="lang-pill">
            <!-- Decorative: the pill's own text names the language, so a flag
                 alt would just repeat it. -->
            <img :src="flagsStore.getFlag(code)" alt="" class="lang-flag" />
            <span class="lang-name">{{ $t(languageKeys[code]) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
import { Languages } from "@/data/Language";
import { flagsStore } from "@/store/countryFlags.js";

// Two rows of roughly equal length, in Language.js declaration order (the
// widely-spoken languages first) rather than the alphabetical-by-English-name
// default export, whose ordering means nothing in the other 30 locales.
const codes = Object.keys(Languages);
const ROW_ONE_LENGTH = Math.ceil(codes.length / 2);

export default {
  name: "LanguagesSection",
  data() {
    return {
      paused: false,
      flagsStore,
      rows: [codes.slice(0, ROW_ONE_LENGTH), codes.slice(ROW_ONE_LENGTH)],
      // Precomputed so the i18n key is a real string in the component rather
      // than a template expression an i18n key extractor cannot see.
      languageKeys: Object.fromEntries(
        codes.map((code) => [code, `languages.${code}`])
      ),
    };
  },
};
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native), so this section has
   no dark-mode branch and stays on the hue tokens the sibling landing sections
   use rather than the role-token layer.

   width: 100%, never 100vw: 100vw includes the scrollbar gutter, which is what
   put a horizontal scrollbar on this page in the first place. overflow: hidden
   is load-bearing rather than defensive here, because .lang-track is wider than
   the viewport by construction.

   Band rhythm: hero gradient -> HowItWorks --al-bg-light -> LearningModes white
   -> this section. --al-surface-muted rather than --al-bg-light because this
   band is white above AND (per the spec) --al-bg-light below once Testimonials
   is restyled; the deeper muted tint is the only fill that keeps a visible
   boundary on both sides, and white pills read harder on it. */
.langs-section {
  background: var(--al-surface-muted);
  width: 100%;
  overflow: hidden;
  padding-bottom: 72px;
}

.langs-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 72px 32px 36px;
}

/* DESIGN.md type scale, H2: 26px / 1.16 / 700. It is not breakpoint-split, so
   there is no mobile size override below. */
.langs-title {
  font-size: 26px;
  /* public/index.html loads Fraunces at discrete 600/700/800. */
  font-weight: 700;
  line-height: 1.16;
  color: var(--al-ink-900);
  text-align: center;
  margin: 0;
  overflow-wrap: break-word;
}

.lang-row {
  overflow: hidden;
  margin-bottom: 12px;
}

.lang-row:last-child {
  margin-bottom: 0;
}

.lang-track {
  display: flex;
  width: max-content;
  /* One loop per language keeps both rows moving at the same speed even though
     they hold a different number of pills. */
  animation: langScroll calc(var(--lang-row-count, 16) * 2.6s) linear infinite;
}

.lang-track-reverse {
  animation-direction: reverse;
}

/* Hovering anywhere on the row pauses it so a name can actually be read; the
   toggle is the pointer-free equivalent and latches until pressed again. */
.lang-row:hover .lang-track,
.lang-track-paused {
  animation-play-state: paused;
}

/* Quiet by design: this is an assistive control, not a call to action, so it
   sits below the heading at caption weight. --al-gray on --al-surface-muted is
   4.8:1. */
.lang-motion-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 16px auto 0;
  padding: 0;
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-pill);
  background: var(--al-white);
  color: var(--al-gray);
  cursor: pointer;
  transition: color var(--al-dur-short) var(--al-ease-move),
    border-color var(--al-dur-short) var(--al-ease-move);
}

.lang-motion-toggle:hover {
  color: var(--al-ink-600);
  border-color: var(--al-gray-400);
}

/* Focus ring recipe from GeoMismatchModal.vue, in --al-ink-600 to match the
   rest of the landing page. */
.lang-motion-toggle:focus-visible {
  outline: 3px solid var(--al-ink-600);
  outline-offset: 2px;
}

.lang-set {
  display: flex;
  gap: 12px;
  padding: 0 12px 0 0;
  margin: 0;
  list-style: none;
}

.lang-pill {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--al-gray-300);
  background: var(--al-white);
  border-radius: var(--al-radius-pill);
  padding: 6px 16px 6px 6px;
  /* DESIGN.md type scale, Body small: 14px / 1.45. --al-gray-700 on white is
     12.9:1. */
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  color: var(--al-gray-700);
  white-space: nowrap;
}

.lang-flag {
  width: 26px;
  height: 26px;
  /* The box is square, so the pill token clamps to a circle; no literal 50%
     is needed to get one. */
  border-radius: var(--al-radius-pill);
  object-fit: cover;
  /* The flag SVGs carry their own white and near-white fields, so without a
     hairline the circle dissolves into the pill. */
  border: 1px solid var(--al-gray-300);
  box-sizing: border-box;
  flex-shrink: 0;
}

@keyframes langScroll {
  from {
    transform: translateX(0);
  }

  to {
    /* Exactly one pass of the row, since the track holds the row twice. */
    transform: translateX(-50%);
  }
}

/* A marquee is decorative motion, so it stops completely rather than slowing
   down: the track becomes a static centred wrap of every language, rendered
   once. */
@media (prefers-reduced-motion: reduce) {
  /* Nothing is moving, so a pause control would be a dead affordance. */
  .lang-motion-toggle {
    display: none;
  }

  .lang-row {
    overflow: visible;
    margin-bottom: 0;
  }

  .lang-track {
    animation: none;
    width: 100%;
  }

  .lang-set {
    flex: 1 1 auto;
    min-width: 0;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0;
    margin-bottom: 12px;
  }

  .lang-set[aria-hidden="true"] {
    display: none;
  }
}

/* Tablet padding matches HeroSection's, HowItWorksSection's and
   LearningModesSection's. */
@media (max-width: 1023px) {
  .langs-section {
    padding-bottom: 56px;
  }

  .langs-inner {
    padding: 56px 24px 28px;
  }
}

@media (max-width: 767px) {
  .langs-section {
    padding-bottom: 48px;
  }

  .langs-inner {
    padding: 48px 16px 24px;
  }
}
</style>
